import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth, useUser, useOrganization } from '@clerk/clerk-react'
import {
  User, Building, Globe, Server, Users, Trash2, AlertTriangle, Save
} from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { getSupabaseWithClerkAuth } from '@/lib/supabase'
import { CURRENCIES, CURRENCY_NAMES, CURRENCY_SYMBOLS } from '@/lib/currency'
import { useToast } from '@/components/ui/toast'

const REGIONS = [
  { value: 'SGP', label: 'Singapore (SGP) — Recommended for APAC' },
  { value: 'AUS', label: 'Australia (AUS) — Recommended for ANZ' },
  { value: 'US', label: 'United States (US) — Fallback' },
]

export default function SettingsPage() {
  const { getToken } = useAuth()
  const { user } = useUser()
  const { organization } = useOrganization()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState('')

  const { data: customer } = useQuery({
    queryKey: ['customer'],
    queryFn: async () => {
      const db = await getSupabaseWithClerkAuth(getToken)
      const { data } = await db.from('customers').select('*').single()
      return data || {
        name: user?.fullName || '',
        company: '',
        currency_preference: 'PHP',
        region_preference: 'SGP',
      }
    },
  })

  const [name, setName] = useState(customer?.name || user?.fullName || '')
  const [company, setCompany] = useState(customer?.company || '')
  const [currency, setCurrency] = useState(customer?.currency_preference || 'PHP')
  const [region, setRegion] = useState(customer?.region_preference || 'SGP')

  // Sync state when customer loads
  React.useEffect(() => {
    if (customer) {
      setName(customer.name || user?.fullName || '')
      setCompany(customer.company || '')
      setCurrency(customer.currency_preference || 'PHP')
      setRegion(customer.region_preference || 'SGP')
    }
  }, [customer, user])

  const profileMutation = useMutation({
    mutationFn: async () => {
      const db = await getSupabaseWithClerkAuth(getToken)
      const { error } = await db
        .from('customers')
        .update({ name, company, currency_preference: currency, region_preference: region })
        .eq('clerk_user_id', user?.id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer'] })
      toast({ title: 'Settings saved', variant: 'success' })
    },
    onError: () => {
      toast({ title: 'Failed to save settings', variant: 'error' })
    },
  })

  const mockOrgMembers = [
    { id: '1', name: user?.fullName || 'You', email: user?.primaryEmailAddress?.emailAddress || '', role: 'Admin' },
    { id: '2', name: 'John Doe', email: 'john@example.com', role: 'Developer' },
    { id: '3', name: 'Jane Smith', email: 'jane@example.com', role: 'Viewer' },
  ]

  return (
    <div className="max-w-2xl space-y-6 animate-fade-in">
      {/* Profile */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-indigo-600" />
            <CardTitle>Profile</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Your name"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={user?.primaryEmailAddress?.emailAddress || ''}
                disabled
                className="bg-gray-50"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="company">Company / Organization</Label>
            <Input
              id="company"
              value={company}
              onChange={e => setCompany(e.target.value)}
              placeholder="Your company name"
            />
          </div>
        </CardContent>
      </Card>

      {/* Preferences */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-indigo-600" />
            <CardTitle>Preferences</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Currency Preference</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map(c => (
                  <SelectItem key={c} value={c}>
                    {CURRENCY_SYMBOLS[c]} {c} — {CURRENCY_NAMES[c]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-gray-400">
              Pricing and costs will be displayed in this currency.
            </p>
          </div>

          <div>
            <Label>API Region</Label>
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REGIONS.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-gray-400">
              Requests will be routed through this region for lowest latency.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end">
        <Button onClick={() => profileMutation.mutate()} loading={profileMutation.isPending}>
          <Save className="h-4 w-4" />
          Save Changes
        </Button>
      </div>

      {/* Team */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-indigo-600" />
              <CardTitle>Team Members</CardTitle>
            </div>
            <Button variant="outline" size="sm">
              Invite Member
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {mockOrgMembers.map(member => (
              <div key={member.id} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium text-sm">
                    {member.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{member.name}</p>
                    <p className="text-xs text-gray-500">{member.email}</p>
                  </div>
                </div>
                <Badge variant={member.role === 'Admin' ? 'default' : 'secondary'}>
                  {member.role}
                </Badge>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-gray-400">
            Invite team members to collaborate on your Neoserve account.
            Uses Clerk Organizations for access management.
          </p>
        </CardContent>
      </Card>

      {/* Danger zone */}
      <Card className="border-red-200">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <CardTitle className="text-red-700">Danger Zone</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between py-3 border border-red-200 rounded-lg px-4">
            <div>
              <p className="text-sm font-medium text-gray-900">Delete Account</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Permanently delete your account and all associated data. This cannot be undone.
              </p>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Account</DialogTitle>
            <DialogDescription>
              This will permanently delete your Neoserve account, all API keys, usage data, and billing history.
              This action <strong>cannot be undone</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>
              Type <strong>DELETE</strong> to confirm
            </Label>
            <Input
              value={deleteConfirm}
              onChange={e => setDeleteConfirm(e.target.value)}
              placeholder="DELETE"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={deleteConfirm !== 'DELETE'}
              onClick={() => {
                toast({
                  title: 'Account deletion requested',
                  description: 'Our team will process your request within 48 hours.',
                  variant: 'default',
                })
                setDeleteOpen(false)
                setDeleteConfirm('')
              }}
            >
              Delete My Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
